import { useEffect, useRef } from "react";
import { useTheme } from "context/ThemeContext";
import { useAppSelector } from "store";
import userService from "api/user/user.service";

/**
 * REF-5756 — Reports the signed-in user's UI theme to the backend so the backoffice can show
 * light/dark adoption. Renders nothing.
 *
 * Persists two values via `PUT users/{id}` (mirrors the LanguageSwitcher → editUserDetail pattern):
 *  - theme_preference: what the user picked in the toggle (light | dark | system)
 *  - theme_effective:  what they actually render (system resolved to light | dark)
 *
 * Fires whenever the pair changes AND a user is present, which covers:
 *  - auth boot / login (captures never-toggled users' current theme)
 *  - a toggle change
 *  - an OS light/dark flip while the preference is "system"
 *
 * MUST be mounted directly under the app-wide ThemeProvider (index.tsx) so it reads the user's
 * real preference — never a route-level forcedTheme provider (RouteThemeWrapper). Best-effort:
 * localStorage stays the per-device source of truth, so a failed write is silently ignored.
 */
const ThemeSync = () => {
  const { theme, effectiveTheme } = useTheme();
  const userId = useAppSelector((state) => state.common.user.id);
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      // Signed out — allow the next session to re-report its boot state.
      lastSent.current = null;

      return;
    }

    const signature = `${theme}:${effectiveTheme}`;
    if (lastSent.current === signature) return;
    lastSent.current = signature;

    userService.editUserDetail(String(userId), { theme_preference: theme, theme_effective: effectiveTheme }).catch(() => {
      // Silent — theme is a best-effort report; localStorage remains authoritative.
      lastSent.current = null;
    });
  }, [userId, theme, effectiveTheme]);

  return null;
};

export default ThemeSync;
